import React, { useState, useMemo } from 'react';
import { Col, Form, FormInstance, Popover, Row, Spin } from 'antd';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { Button, Tooltip } from '../../../components/index';
import { FilterContentCheckbox } from './filter-content-checkbox';
import { FilterContentRadio } from './filter-content-radio';
import { FilterContentSelect } from './filter-content-select';
import { FilterContentTimeRange } from './filter-content-time-range';
import { FilterContentDateRange } from './filter-content-date-range';
import { FilterContentTextCondition } from './filter-content-text-condition';
import { FilterContentNumberCondition } from './filter-content-number-condition';
import { FilterDropdownSearch } from './filter-dropdown-search';
import { Translations } from '../filter-bar-2';
import { FilterContentTeams } from './filter-content-teams';
import { FilterBar2FilterType, FilterBar2Values, FilterBar2ListItem, FilterBar2Value } from '../types';
import useFilterChangeHandler from './internal/use-filter-change-handler';
import { FilterContentTreeSelect } from './filter-content-tree-select';
import { FilterContentDate } from './filter-content-date';
import { DateRangeV2 } from './date-range-v2';

export type FilterBaseProps = FilterBar2ListItem & {
	children: Function;
	form: FormInstance;
	translations: Translations;
	onCancel: Function;
	popoverContainer: HTMLElement;
	handleChangeDebounceMs?: number;
	minValue?: number;
};

export const FilterBase = (props: FilterBaseProps) => {
	const {
		children,
		type,
		title,
		name,
		form,
		placeholder,
		tooltip,
		loading,
		translations,
		temporaryOpened,
		onCancel,
		applyFilterOnValueChange,
		enforceItemUpdate,
		handleChangeDebounceMs = 500,
		popoverContainer,
		minValue
	} = props;

	const [isVisible, setIsVisible] = useState<boolean>(temporaryOpened || false);
	const [prevState, setPrevState] = useState<FilterBar2Values>({ [name]: form.getFieldValue(name) });

	const handleCancel = () => {
		setIsVisible(false);
		form.setFieldsValue(prevState);
		onCancel();
	};

	const handleDone = () => {
		setIsVisible(false);
		form.submit();
	};

	const handleVisibleChange = async isVisible => {
		if (isVisible) {
			setIsVisible(true);
			setPrevState(form.getFieldsValue([name]));
		} else if (applyFilterOnValueChange) {
			setIsVisible(false);
		} else {
			handleCancel();
		}
	};

	const handleFilterChange = useFilterChangeHandler({
		applyFilterOnValueChange,
		debounceMs: handleChangeDebounceMs,
		form
	});

	const sortValuesByLabel = (values: FilterBar2Value[]) =>
		values.sort((a, b) => a.label?.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true }));

	const getContent = (): JSX.Element | null => {
		// use onFilterChange listener for each filter type component till the atnd not updated
		switch (type) {
			case FilterBar2FilterType.CUSTOM: {
				const { component, values, form } = props;
				return component({ name, values, form, handleFilterChange });
			}
			case FilterBar2FilterType.CHECKBOX:
				return (
					<FilterContentCheckbox
						name={name}
						translations={translations}
						values={props.values}
						onFilterChange={handleFilterChange}
						form={form}
						disableSelectAll={props.disableSelectAll}
						selectAllTooltipThreshold={props.selectAllTooltipThreshold}
						isVisible={isVisible}
						allowSelectAll={props.allowSelectAll}
						selectAllThreshold={props.selectAllThreshold}
						selectAllTooltip={props.selectAllTooltip}
						onSearch={props.customProps?.onSearch}
					/>
				);
			case FilterBar2FilterType.RADIO:
				return (
					<FilterContentRadio
						name={name}
						values={props.values}
						onFilterChange={handleFilterChange}
						customAllAsOption={props.customAllAsOption}
					/>
				);
			case FilterBar2FilterType.TREE_SELECT: {
				const { selectAllTagText, showCheckedStrategy } = props;

				return (
					<FilterContentTreeSelect
						name={name}
						placeholder={placeholder}
						form={form}
						values={props.values as FilterBar2Value[]}
						onChange={handleFilterChange}
						translations={{ ...translations, selectAllTagText }}
						showCheckedStrategy={showCheckedStrategy}
					/>
				);
			}
			case FilterBar2FilterType.SELECT: {
				const {
					multiselect,
					customProps = {},
					values,
					allowSelectAll,
					selectAllThreshold,
					selectAllTagText,
					maxTagCount,
					maxTagPlaceholder,
					emptyAsAll
				} = props;
				return (
					<FilterContentSelect
						placeholder={placeholder}
						multiselect={multiselect}
						name={name}
						values={sortValuesByLabel(values as FilterBar2Value[])}
						translations={translations}
						onChange={handleFilterChange}
						onSearch={customProps.onSearch}
						form={form}
						allowSelectAll={allowSelectAll}
						emptyAsAll={emptyAsAll}
						maxTagCount={maxTagCount}
						maxTagPlaceholder={maxTagPlaceholder}
						selectAllThreshold={selectAllThreshold}
						selectAllTagText={selectAllTagText}
					/>
				);
			}
			case FilterBar2FilterType.DATE: {
				return (
					<FilterContentDate
						name={name}
						onFilterChange={handleFilterChange}
						datePickerProps={props.customProps || {}}
					/>
				);
			}
			case FilterBar2FilterType.TIME_RANGE:
				return <FilterContentTimeRange name={name} onFilterChange={handleFilterChange} />;
			case FilterBar2FilterType.DATE_RANGE:
				return (
					<FilterContentDateRange
						name={name}
						datePickerProps={props.customProps || {}}
						onFilterChange={handleFilterChange}
						translations={translations}
						form={form}
					/>
				);
			case FilterBar2FilterType.DATE_RANGE_V2:
				return (
					<DateRangeV2
						name={name}
						datePickerProps={props.customProps || {}}
						onFilterChange={handleFilterChange}
						translations={translations}
						form={form}
					/>
				);
			case FilterBar2FilterType.TEXT_CONDITION:
				return (
					<FilterContentTextCondition
						name={name}
						translations={translations}
						form={form}
						onFilterChange={handleFilterChange}
					/>
				);
			case FilterBar2FilterType.NUMBER_CONDITION:
				return (
					<FilterContentNumberCondition
						name={name}
						translations={translations}
						form={form}
						minValue={minValue}
						onFilterChange={handleFilterChange}
					/>
				);
			case FilterBar2FilterType.DROPDOWN_SEARCH:
				return (
					<FilterDropdownSearch name={name} placeholder={placeholder} onFilterChange={handleFilterChange} />
				);
			case FilterBar2FilterType.TEAMS:
				return (
					<FilterContentTeams
						placeholder={placeholder}
						multiselect={props.multiselect}
						name={name}
						values={sortValuesByLabel(props.values as FilterBar2Value[])}
						translations={translations}
						onChange={handleFilterChange}
						form={form}
					/>
				);
			default:
				return null;
		}
	};

	const itemViewShouldUpdate = useMemo(() => (applyFilterOnValueChange ?? true) || enforceItemUpdate, [
		applyFilterOnValueChange,
		enforceItemUpdate
	]);

	return (
		<Popover
			open={isVisible}
			onOpenChange={handleVisibleChange}
			trigger="click"
			placement="bottomLeft"
			overlayClassName="filter-bar-2-overlay"
			getPopupContainer={() => popoverContainer}
			content={
				<Spin spinning={Boolean(loading)}>
					<div
						data-test-id={`filter-dropdown-${name}`}
						className={`filter-bar-2-base filter-bar-2-base-${type}`}
					>
						<h5 className="filter-bar-2-base-title">
							{title}
							{tooltip ? (
								<Tooltip className="filter-bar-2-tooltip" title={tooltip}>
									<BringgIcon
										iconName={BringgFontIcons.CircleExclamation}
										className="title-info-icon"
									/>
								</Tooltip>
							) : null}
						</h5>

						{getContent()}

						{!applyFilterOnValueChange && (
							<Row gutter={[34, 0]} justify="end">
								<Col>
									<Button
										block
										type="link"
										className="filter-bar-2-button-blank"
										onClick={handleCancel}
									>
										{translations.cancel}
									</Button>
								</Col>

								<Col>
									<Button
										block
										type="primary"
										className="filter-bar-2-button-fill"
										onClick={handleDone}
									>
										{translations.done}
									</Button>
								</Col>
							</Row>
						)}
					</div>
				</Spin>
			}
		>
			<span className="filter-bar-2-tag-wrapper" data-test-id={`filter-bar-2-tag-wrapper-${props.name}`}>
				<Form.Item noStyle shouldUpdate={itemViewShouldUpdate}>
					{() => children({ isVisible })}
				</Form.Item>
			</span>
		</Popover>
	);
};
